import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Mail, ShieldCheck, UserPlus } from 'lucide-react';
import { Button, Card, Input } from '@nako/ui';
import { BrandTagline } from '@/components/BrandTagline';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { supabase } from '@/lib/supabase';
import { makeT } from '@/lib/i18n';

type Mode = 'password' | 'magic' | 'signup';
type Step = 'credentials' | 'mfa';

export default function Login() {
  const session = useAuthStore((s) => s.session);
  const loadProfileAndBusiness = useAuthStore((s) => s.loadProfileAndBusiness);
  const lang = useUiStore((s) => s.lang);
  const t = makeT(lang);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const initialMode: Mode = searchParams.get('signup') ? 'signup' : 'password';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sent, setSent] = useState(false);
  const [signupConfirmSent, setSignupConfirmSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MFA challenge state — populated after password sign-in if user has TOTP
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  if (session && step !== 'mfa') return <Navigate to="/app" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (mode === 'signup') {
      if (password.length < 8) {
        setLoading(false);
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setLoading(false);
        setError('The two passwords do not match.');
        return;
      }
      const { data, error: err } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/signup` },
      });
      setLoading(false);
      if (err) {
        setError(err.message);
        return;
      }
      // If email confirmation is disabled in Supabase, signUp returns a
      // session immediately and we drop the user straight into business
      // setup. Otherwise, show the "check your email" state.
      if (data.session) {
        await loadProfileAndBusiness(data.session.user.id);
        navigate('/signup');
      } else {
        setSignupConfirmSent(true);
      }
      return;
    }

    if (mode === 'password') {
      const { error: err, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (err) {
        setLoading(false);
        // Friendlier message + nudge toward signup for the most common case.
        if (/invalid login credentials/i.test(err.message)) {
          setError(
            "We couldn't sign you in with that email and password. If you haven't signed up yet, switch to Create account below.",
          );
        } else {
          setError(err.message);
        }
        return;
      }

      // Did this account enrol a TOTP factor? If so, we need to upgrade
      // the session to AAL2 before letting them through.
      const aal = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal.data?.nextLevel === 'aal2' && aal.data.currentLevel === 'aal1') {
        const factors = await supabase.auth.mfa.listFactors();
        const verified = (factors.data?.totp ?? []).find((f) => f.status === 'verified');
        if (verified) {
          const challenge = await supabase.auth.mfa.challenge({ factorId: verified.id });
          if (challenge.error || !challenge.data) {
            setLoading(false);
            setError(challenge.error?.message ?? 'Could not start 2FA challenge.');
            return;
          }
          setMfaFactorId(verified.id);
          setMfaChallengeId(challenge.data.id);
          setStep('mfa');
          setLoading(false);
          return;
        }
      }

      // No MFA — straight through.
      if (data.session) await loadProfileAndBusiness(data.session.user.id);
      setLoading(false);
      navigate('/app');
      return;
    }

    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/app` },
    });
    setLoading(false);
    if (err) setError(err.message);
    else setSent(true);
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || !mfaChallengeId) return;
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: mfaChallengeId,
      code: mfaCode,
    });
    if (err) {
      setLoading(false);
      setError(err.message);
      return;
    }
    const { data: ses } = await supabase.auth.getSession();
    if (ses.session) await loadProfileAndBusiness(ses.session.user.id);
    setLoading(false);
    navigate('/app');
  };

  const cancelMfa = async () => {
    await supabase.auth.signOut();
    setStep('credentials');
    setMfaCode('');
    setMfaFactorId(null);
    setMfaChallengeId(null);
    setError(null);
  };

  return (
    <main className="container-mobile py-12">
      <Link
        to="/"
        className="text-muted hover:text-ink mb-6 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>

      <header className="mb-8 text-center">
        <h1 className="font-display text-4xl font-semibold">{t('app.name')}</h1>
        <BrandTagline className="mt-2" />
        <p className="text-muted mt-2 text-sm">{t('app.tagline')} — Tsamaya le Pula</p>
      </header>

      <Card>
        <h2 className="font-display mb-4 text-2xl">{t('auth.login.title')}</h2>

        {signupConfirmSent ? (
          <div className="py-6 text-center">
            <p className="text-green mb-2 font-medium">📧 Confirm your email</p>
            <p className="text-muted text-sm">
              We sent a confirmation link to <span className="font-mono">{email}</span>. Click it
              and you'll land back here, signed in.
            </p>
            <button
              onClick={() => {
                setSignupConfirmSent(false);
                setMode('password');
                setError(null);
              }}
              className="text-accent mt-4 text-xs underline"
            >
              Use a different email instead
            </button>
          </div>
        ) : step === 'mfa' ? (
          <form onSubmit={submitMfa} className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <p className="flex items-center gap-2 font-medium">
                <ShieldCheck size={14} /> Two-factor authentication
              </p>
              <p className="mt-1 text-emerald-800">
                Open Microsoft Authenticator (or whichever app you set up) and type the 6-digit code
                shown for Nako Pula.
              </p>
            </div>
            <Input
              label="6-digit code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              autoComplete="one-time-code"
              autoFocus
              error={error ?? undefined}
            />
            <Button type="submit" full loading={loading} disabled={mfaCode.length < 6}>
              Verify and continue
            </Button>
            <button
              type="button"
              onClick={() => void cancelMfa()}
              className="text-muted hover:text-ink mx-auto block text-xs"
            >
              Cancel and start over
            </button>
          </form>
        ) : sent ? (
          <div className="py-6 text-center">
            <p className="text-green mb-2 font-medium">📧 {t('auth.check_inbox')}</p>
            <p className="text-muted text-sm">
              We sent a sign-in link to <span className="font-mono">{email}</span>.
            </p>
            <button
              onClick={() => {
                setSent(false);
                setError(null);
              }}
              className="text-accent mt-4 text-xs underline"
            >
              Use a different email or password instead
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-pill bg-paper-2 mb-4 inline-flex p-0.5 text-xs">
              <button
                type="button"
                onClick={() => {
                  setMode('password');
                  setError(null);
                }}
                className={`rounded-pill flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${
                  mode === 'password' ? 'bg-ink text-paper' : 'text-muted'
                }`}
              >
                <KeyRound size={12} /> Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('magic');
                  setError(null);
                }}
                className={`rounded-pill flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${
                  mode === 'magic' ? 'bg-ink text-paper' : 'text-muted'
                }`}
              >
                <Mail size={12} /> Magic link
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('signup');
                  setError(null);
                }}
                className={`rounded-pill flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${
                  mode === 'signup' ? 'bg-amber-500 text-white' : 'text-muted'
                }`}
              >
                <UserPlus size={12} /> Create account
              </button>
            </div>

            {mode === 'signup' && (
              <p className="text-muted mb-3 text-xs">
                New here? Create your account in 30 seconds — no card needed, 30 days free.
              </p>
            )}

            <form onSubmit={submit} className="space-y-4">
              <Input
                type="email"
                label={t('auth.email')}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />

              {(mode === 'password' || mode === 'signup') && (
                <div className="space-y-2">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    label={mode === 'signup' ? 'Choose a password' : 'Password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    hint={mode === 'signup' ? 'At least 8 characters' : undefined}
                    error={mode === 'password' ? (error ?? undefined) : undefined}
                  />
                  {mode === 'signup' && (
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      label="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      error={error ?? undefined}
                    />
                  )}
                  <label className="text-muted mt-1 flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                    />
                    Show password
                  </label>
                </div>
              )}

              {mode === 'magic' && error && <p className="text-red text-sm">{error}</p>}

              <Button type="submit" full loading={loading}>
                {mode === 'password'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : t('auth.send_link')}
              </Button>

              {mode === 'password' && (
                <p className="text-muted text-center text-xs">
                  Forgot your password?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('magic');
                      setError(null);
                    }}
                    className="text-accent underline"
                  >
                    Sign in with a magic link
                  </button>
                </p>
              )}
            </form>
          </>
        )}
      </Card>

      <p className="text-muted mt-8 text-center text-xs">v0.1 • Nako Tech Pty Ltd</p>
    </main>
  );
}
